import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Button,
  Spinner,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  createSoaSchema,
  type CreateSoaFormData,
} from "@/lib/validations/auth";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

interface LineItemColumn {
  id: number;
  name: string;
}

interface InvalidBookingEntry {
  booking?: { id?: number; reference_number?: string };
  reason?: string;
}

interface StatementOfAccountFormProps {
  shippingLineId: string;
  shippingLineName: string;
  selectedBookingIds: number[];
  soaId?: number;
  /** Expected date range from Financial Statements (filters bookings by shipping line) */
  expectedDateFrom?: string;
  expectedDateTo?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function StatementOfAccountForm({
  shippingLineId,
  shippingLineName,
  selectedBookingIds,
  soaId,
  expectedDateFrom,
  expectedDateTo,
  onSuccess,
  onCancel,
}: StatementOfAccountFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [lineItemsData, setLineItemsData] = useState<Record<string, unknown>[]>(
    [],
  );
  const [lineItemColumns, setLineItemColumns] = useState<LineItemColumn[]>([]);
  const [finalBookingIds, setFinalBookingIds] =
    useState<number[]>(selectedBookingIds);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const isEditMode = !!soaId;
  const isMountedRef = useRef(true);
  const [originalBookingIds, setOriginalBookingIds] = useState<number[]>([]);
  const [addedBookingIds, setAddedBookingIds] = useState<number[]>([]);
  const [availableBookings, setAvailableBookings] = useState<any[]>([]);
  const [selectedBookingToAdd, setSelectedBookingToAdd] = useState<string>("");
  const [isLoadingAvailableBookings, setIsLoadingAvailableBookings] =
    useState(false);
  const [isValidatingAddBooking, setIsValidatingAddBooking] = useState(false);
  const [invalidBookingsModalOpen, setInvalidBookingsModalOpen] =
    useState(false);
  const [invalidBookings, setInvalidBookings] = useState<InvalidBookingEntry[]>(
    [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateSoaFormData>({
    resolver: zodResolver(createSoaSchema),
    defaultValues: {
      dli_sa_number: "",
      work_order: "",
    },
  });

  // Fetch SOA data when in edit mode
  useEffect(() => {
    if (!isEditMode || !soaId) return;
    isMountedRef.current = true;
    const fetchSoaData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/soa/${soaId}`);
        if (!isMountedRef.current) return;
        const soaData = response.data?.data || response.data || {};

        reset({
          dli_sa_number: soaData.dli_sa_number || "",
          work_order: soaData.work_order || "",
        });

        // Extract booking IDs from SOA data
        const bookingIds = soaData.booking_ids || [];
        setFinalBookingIds(
          bookingIds.length > 0 ? bookingIds : selectedBookingIds,
        );
        setOriginalBookingIds(
          bookingIds.length > 0 ? bookingIds : selectedBookingIds,
        );
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchSoaData();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, soaId, reset, showError, selectedBookingIds]);

  // Fetch available bookings in edit mode
  useEffect(() => {
    if (!isEditMode || !shippingLineId) return;
    
    isMountedRef.current = true;
    const fetchAvailableBookings = async () => {
      setIsLoadingAvailableBookings(true);
      try {
        const params: Record<string, any> = {};
        if (expectedDateFrom) params.expected_date_from = expectedDateFrom;
        if (expectedDateTo) params.expected_date_to = expectedDateTo;
        params.is_complete = 0;
        const response = await apiClient.get(
          `/bookings/by-shipping-line/${shippingLineId}`,
          Object.keys(params).length > 0 ? { params } : undefined,
        );
        if (!isMountedRef.current) return;
        
        const bookingsData = response.data?.data || [];
        setAvailableBookings(Array.isArray(bookingsData) ? bookingsData : []);
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error);
          setAvailableBookings([]);
        }
      } finally {
        if (isMountedRef.current) setIsLoadingAvailableBookings(false);
      }
    };

    fetchAvailableBookings();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, shippingLineId, expectedDateFrom, expectedDateTo, showError]);

  // Fetch line items from /soa/line-items (shipping_line_id + booking_ids, no per_page)
  useEffect(() => {
    const bookingIdsToUse =
      finalBookingIds.length > 0 ? finalBookingIds : selectedBookingIds;
    if (bookingIdsToUse.length === 0 || !shippingLineId) {
      setLineItemsData([]);
      setLineItemColumns([]);
      return;
    }
    isMountedRef.current = true;
    const fetchLineItems = async () => {
      setIsLoadingBookings(true);
      try {
        const response = await apiClient.get("/soa/line-items", {
          params: {
            shipping_line_id: shippingLineId,
            booking_ids: bookingIdsToUse,
          },
        });

        if (!isMountedRef.current) return;
        const data = response.data?.data ?? [];
        const columns = response.data?.columns ?? [];
        setLineItemsData(Array.isArray(data) ? data : []);
        setLineItemColumns(Array.isArray(columns) ? columns : []);
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error);
          setLineItemsData([]);
          setLineItemColumns([]);
        }
      } finally {
        if (isMountedRef.current) setIsLoadingBookings(false);
      }
    };

    fetchLineItems();
    return () => {
      isMountedRef.current = false;
    };
  }, [finalBookingIds, selectedBookingIds, shippingLineId, showError]);

  // Format cell value: amount-like columns get "P " prefix when numeric or numeric string
  const formatCellValue = (value: unknown, columnName: string): string => {
    if (value == null || value === "") return "-";
    const name = String(columnName).toLowerCase();
    const isAmountColumn =
      name.includes("amount") || name.includes("vat") || name === "total";
    if (isAmountColumn && typeof value === "number") {
      return `P ${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    if (isAmountColumn && typeof value === "string" && value !== "") {
      return `P ${value}`;
    }
    return String(value);
  };

  // Pagination calculations
  const totalPages = Math.ceil(lineItemsData.length / itemsPerPage);
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return lineItemsData.slice(startIndex, endIndex);
  }, [lineItemsData, currentPage, itemsPerPage]);

  // Reset to page 1 when data length changes
  useEffect(() => {
    setCurrentPage(1);
  }, [lineItemsData.length]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const applyAddBooking = (bookingId: number) => {
    const updatedBookingIds = [...finalBookingIds, bookingId];
    setFinalBookingIds(updatedBookingIds);
    setAddedBookingIds([...addedBookingIds, bookingId]);
    setSelectedBookingToAdd("");
  };

  const handleAddBooking = async () => {
    if (!selectedBookingToAdd) return;

    const bookingId = parseInt(selectedBookingToAdd, 10);
    if (Number.isNaN(bookingId)) return;

    if (finalBookingIds.includes(bookingId)) {
      showError("This booking is already added");
      return;
    }

    setIsValidatingAddBooking(true);
    try {
      const response = await apiClient.post("/soa-billing-check/validate", {
        booking_ids: [bookingId],
        type: 1,
      });

      const payload = response.data ?? {};
      const invalidList: InvalidBookingEntry[] = Array.isArray(
        payload.invalid_bookings,
      )
        ? payload.invalid_bookings
        : [];

      if (invalidList.length > 0) {
        setInvalidBookings(invalidList);
        setInvalidBookingsModalOpen(true);
        return;
      }

      applyAddBooking(bookingId);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { invalid_bookings?: InvalidBookingEntry[] } };
      };
      const invalidFromError = err.response?.data?.invalid_bookings;
      if (Array.isArray(invalidFromError) && invalidFromError.length > 0) {
        setInvalidBookings(invalidFromError);
        setInvalidBookingsModalOpen(true);
        return;
      }
      showError(error);
    } finally {
      setIsValidatingAddBooking(false);
    }
  };

  const handleCancelWithReset = () => {
    // Reset to original booking IDs
    setFinalBookingIds(originalBookingIds);
    setAddedBookingIds([]);
    setSelectedBookingToAdd("");
    setInvalidBookingsModalOpen(false);
    setInvalidBookings([]);
    onCancel();
  };

  const onSubmit = async (data: CreateSoaFormData) => {
    // Validate booking_ids separately since it's not a form field
    const bookingIdsToUse =
      finalBookingIds.length > 0 ? finalBookingIds : selectedBookingIds;
    if (!bookingIdsToUse || bookingIdsToUse.length === 0) {
      showError("At least one booking is required");
      return;
    }

    if (!shippingLineId) {
      showError("Shipping line ID is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        shipping_line_id: shippingLineId,
        dli_sa_number: data.dli_sa_number.trim(),
        booking_ids: bookingIdsToUse,
        work_order: data.work_order?.trim() || null,
      };

      if (isEditMode && soaId) {
        await apiClient.put(`/soa/${soaId}`, payload);
        showSuccess("Success", "SOA updated successfully");
      } else {
        await apiClient.post("/soa/generate", payload);
        showSuccess("Success", "SOA created successfully");
      }

      onSuccess?.();
    } catch (error: any) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading SOA data...
        </Text>
      </Box>
    );
  }

  return (
    <Box minW={0} w="100%" maxW="100%">
      <VStack spacing={4} align="stretch">
        {/* Form Section */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4} align="stretch">
              {/* Shipping Line Field (Read-only) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  Shipping Line: {" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    value={shippingLineName}
                    readOnly
                    bg={colors.sectionBg}
                    borderColor={colors.border}
                  />
                </FormControl>
              </Grid>

              {/* DLI-SA Number Field */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  DLI-SA Number: {" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.dli_sa_number}>
                  <Input
                    {...register("dli_sa_number")}
                    placeholder="Please input DLI-SA no"
                    borderColor={colors.border}
                  />
                  <FormErrorMessage>
                    {errors.dli_sa_number?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Work Order Number Field */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  Work Order Number:
                </FormLabel>
                <FormControl isInvalid={!!errors.work_order}>
                  <Input
                    {...register("work_order")}
                    placeholder="Please input work order no (optional)"
                    borderColor={colors.border}
                  />
                  <FormErrorMessage>
                    {errors.work_order?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Add Booking/s Field - Only in Edit Mode */}
              {isEditMode && (
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium">
                    Add Booking/s:
                  </FormLabel>
                  <HStack spacing={2}>
                    <FormControl flex={1}>
                      <Select
                        value={selectedBookingToAdd}
                        onChange={(e) => setSelectedBookingToAdd(e.target.value)}
                        placeholder="Select booking to add"
                        borderColor={colors.border}
                        isDisabled={isLoadingAvailableBookings}
                      >
                        {availableBookings
                          .filter((booking) => !finalBookingIds.includes(booking.id))
                          .map((booking) => (
                            <option key={booking.id} value={booking.id}>
                              {booking.reference_number}
                            </option>
                          ))}
                      </Select>
                    </FormControl>
                    <Button
                      leftIcon={<Icon as={Plus} boxSize={4} />}
                      onClick={() => void handleAddBooking()}
                      colorScheme="blue"
                      isDisabled={
                        !selectedBookingToAdd ||
                        isLoadingAvailableBookings ||
                        isValidatingAddBooking
                      }
                      isLoading={isValidatingAddBooking}
                      loadingText="Validating..."
                    >
                      Add
                    </Button>
                  </HStack>
                </Grid>
              )}
            </VStack>
            </Box>
          </CardBody>
        </Card>

        {/* Table Section: scroll horizontally when wide, keep sidebar visible */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border} minW={0}>
          <CardBody p={0} minW={0}>
            {isLoadingBookings ? (
            <Box p={8} textAlign="center">
              <Spinner size="lg" color="blue.500" />
              <Text mt={4} color={colors.text}>
                Loading details...
              </Text>
            </Box>
            ) : lineItemsData.length === 0 ? (
              <Box p={8} textAlign="center">
                <Text color={colors.textSecondary}>
                  {isEditMode ? "No records found" : "No bookings selected"}
                </Text>
              </Box>
            ) : (
              <Box minW={0} overflowX="auto">
                <TableContainer minW="min-content">
                  <Table variant="simple" size="sm">
                    <Thead bg={colors.sectionBg}>
                      <Tr>
                        {lineItemColumns.map((col) => {
                          const isNumeric = /amount|vat|total/i.test(col.name);
                          return (
                            <Th
                              key={col.id}
                              borderColor={colors.border}
                              color={colors.text}
                              isNumeric={isNumeric}
                            >
                              {col.name}
                            </Th>
                          );
                        })}
                      </Tr>
                    </Thead>
                  <Tbody>
                    {paginatedRows.map((row, index) => (
                      <Tr key={index}>
                        {lineItemColumns.map((col) => {
                          const value = row[col.name];
                          const isNumeric = /amount|vat|total/i.test(col.name);
                          return (
                            <Td
                              key={col.id}
                              borderColor={colors.border}
                              color={colors.text}
                              isNumeric={isNumeric}
                            >
                              {formatCellValue(value, col.name)}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                </TableContainer>
              </Box>
            )}
            {/* Pagination Controls */}
            {lineItemsData.length > 0 && (
              <Box
                p={4}
                borderTopWidth="1px"
                borderColor={colors.border}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text fontSize="sm" color={colors.text}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, lineItemsData.length)} of{" "}
                  {lineItemsData.length} entries
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Previous page"
                    icon={<Icon as={ChevronLeft} boxSize={4} />}
                    size="sm"
                    onClick={handlePreviousPage}
                    isDisabled={currentPage === 1}
                    variant="ghost"
                  />
                  <Text
                    fontSize="sm"
                    color={colors.text}
                    minW="60px"
                    textAlign="center"
                  >
                    Page {currentPage} of {totalPages}
                  </Text>
                  <IconButton
                    aria-label="Next page"
                    icon={<Icon as={ChevronRight} boxSize={4} />}
                    size="sm"
                    onClick={handleNextPage}
                    isDisabled={currentPage === totalPages}
                    variant="ghost"
                  />
                </HStack>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Invalid bookings modal */}
        <Modal
          isOpen={invalidBookingsModalOpen}
          onClose={() => setInvalidBookingsModalOpen(false)}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent bg={colors.cardBg} borderColor={colors.border}>
            <ModalHeader color={colors.text}>Invalid Bookings</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text color={colors.textSecondary} mb={3}>
                The following booking(s) cannot be added:
              </Text>
              <UnorderedList spacing={2} color={colors.text}>
                {invalidBookings.map((item, idx) => {
                  const ref =
                    item.booking?.reference_number ??
                    (item.booking?.id != null
                      ? `Booking #${item.booking.id}`
                      : null);
                  return (
                    <ListItem key={item.booking?.id ?? idx}>
                      {ref ?? "Booking"}
                      {item.reason && (
                        <Text as="span" color={colors.textSecondary} ml={1}>
                          — {item.reason}
                        </Text>
                      )}
                    </ListItem>
                  );
                })}
              </UnorderedList>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={() => setInvalidBookingsModalOpen(false)}
              >
                OK
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={2}>
          <Button
            variant="outline"
            onClick={handleCancelWithReset}
            borderColor={colors.border}
            color={colors.text}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
          >
            {isEditMode ? "Update SOA" : "Create SOA"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
